import React, { useState } from 'react';
import {
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Box,
    Link,
} from '@mui/material';
import { UserType } from '../../../common/commonTypes';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { createStripeCheckoutSession } from '../../../api';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

type ModalSubscribeType = {
    user: UserType;
    onCancel: () => void;
    isInitial?: boolean;
    // onSuccess: () => void;
};

const priceIds = {
    monthly: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID as string,
    yearly: process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID as string,
};

const SubscribeModal = (props: ModalSubscribeType) => {
    const { isInitial, user, onCancel } = props;

    const { t, i18n } = useTranslation();

    const [selectedPlan, setSelectedPlan] = useState<string>('monthly');
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedPlan(event.target.value);
    };

    const handleSubscribe = async () => {
        setLoading(true);
        setErrorMessage('');

        const priceId: string = selectedPlan === 'monthly' ? priceIds.monthly : priceIds.yearly;

        try {
            const response = await createStripeCheckoutSession(
                user.id,
                priceId,
                user.settings.languageCode
            );
            setLoading(false);
        } catch (error: any) {
            console.log(
                '%c Error creating stripe session >>> ',
                'background: #333; color: #bada55',
                error
            );

            const message =
                error.response.data.message ||
                error.response.data.error ||
                'Error creating stripe session';
            setErrorMessage(message);
            setLoading(false);
        }
    };

    return (
        <Dialog
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onCancel();
                }
            }}
        >
            <DialogTitle>{isInitial ? t('trial_expired') : t('select_plan')}</DialogTitle>
            <DialogContent>
                {isInitial && <p className="mb-4 text-sm">{t('please_select_plan_to_continue')}</p>}

                {Boolean(errorMessage) && (
                    <Alert severity="error" className="mb-4">
                        {errorMessage}
                    </Alert>
                )}

                <FormControl>
                    <RadioGroup value={selectedPlan} onChange={handlePlanChange}>
                        <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                            <FormControlLabel
                                value="monthly"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle1">
                                            {t('monthly_plan')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('monthly_plan_price')}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                            <FormControlLabel
                                value="annual"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="subtitle1">
                                            {t('annual_plan_save')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('annual_plan_price')}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
                <p className="text-xs mt-4">
                    {t('by_using_agree')} &nbsp;
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href={`https://calpal.pro/terms-${i18n.language}.html`}
                    >
                        {t('with_tos')} <OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
                    </Link>
                </p>
            </DialogContent>
            <DialogActions>
                <LoadingButton onClick={onCancel} disabled={loading}>
                    {t('MODAL_cancel')}
                </LoadingButton>
                <LoadingButton
                    onClick={handleSubscribe}
                    loading={loading}
                    variant="contained"
                    color="success"
                >
                    {t('MODAL_subscribe')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SubscribeModal;
